import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useCountry } from "@/lib/CountryContext";
import { Field } from "./Companies";
import QuoteBuilder from "@/components/QuoteBuilder";

const STAGES = ["qualification", "proposal", "negotiation", "won", "lost"];

const STAGE_STYLE = {
  qualification: "border-gray-400 text-gray-600 bg-gray-50",
  proposal: "border-[#2563EB] text-[#2563EB] bg-blue-50",
  negotiation: "border-[#D97706] text-[#D97706] bg-amber-50",
  won: "border-[#059669] text-[#059669] bg-emerald-50",
  lost: "border-red-400 text-red-600 bg-red-50",
};

function fmtMoney(v) {
  if (!v) return "$0";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v}`;
}

function emptyForm() {
  return {
    title: "", company_id: "", project_id: "", product_ids: [],
    stage: "qualification", value_usd: 0, probability: 20,
    expected_close: "", notes: "",
  };
}

export default function Opportunities() {
  const { country } = useCountry();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/opportunities"),
      api.get("/companies", { params: { country } }),
      api.get("/projects", { params: { country } }),
      api.get("/products"),
    ]);
    // Filter opps by company country
    const cIds = new Set(b.data.map((c) => c.id));
    setItems(a.data.filter((o) => cIds.has(o.company_id)));
    setCompanies(b.data);
    setProjects(c.data);
    setProducts(d.data);
  };

  useEffect(() => { load(); }, [country]); // eslint-disable-line

  const cName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const pName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm(), ...item });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.value_usd = parseFloat(payload.value_usd || 0);
      payload.probability = parseInt(payload.probability || 0);
      if (!payload.project_id) payload.project_id = null;
      if (!payload.expected_close) payload.expected_close = null;
      if (editing) await api.put(`/opportunities/${editing.id}`, payload);
      else await api.post("/opportunities", payload);
      toast.success(editing ? "Updated" : "Created");
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const generatePdf = (opp) => {
    setQuoteFor(opp);
  };

  // Group by stage for kanban
  const byStage = STAGES.reduce((acc, s) => {
    acc[s] = items.filter((i) => i.stage === s);
    return acc;
  }, {});

  const totals = STAGES.map((s) =>
    items.filter((i) => i.stage === s).reduce((a, b) => a + (b.value_usd || 0), 0)
  );

  return (
    <div data-testid="opportunities-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow">Sales</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Opportunities</h1>
          <p className="text-sm text-gray-500 mt-2">{items.length} deals across pipeline</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-opportunity-button">
          <Plus size={16} /> New Opportunity
        </button>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="opportunities-kanban">
        {STAGES.map((s, idx) => (
          <div key={s} className="section-card flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className={`chip ${STAGE_STYLE[s]}`}>{s}</span>
                <span className="text-xs text-gray-400 font-mono">{byStage[s].length}</span>
              </div>
              <div className="font-display text-base font-bold mt-2">{fmtMoney(totals[idx])}</div>
            </div>
            <div className="p-2 flex-1 space-y-2 min-h-[200px]">
              {byStage[s].map((o) => (
                <div key={o.id}
                  className="bg-white border border-gray-200 p-3 hover:border-[#004B87] cursor-pointer transition-colors"
                  onClick={() => openEdit(o)}
                  data-testid={`opportunity-card-${o.id}`}
                >
                  <div className="font-semibold text-sm mb-1 line-clamp-2">{o.title}</div>
                  <div className="text-xs text-gray-500 mb-2 line-clamp-1">{cName(o.company_id)}</div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-[#004B87]">{fmtMoney(o.value_usd)}</span>
                    <span className="text-gray-400">{o.probability}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Table for full list */}
      <div className="mt-6 section-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="stark-table">
          <thead>
            <tr>
              <th>Title</th><th>Company</th><th>Project</th><th>Stage</th><th>Value</th><th>Prob.</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id}>
                <td className="font-semibold">{o.title}</td>
                <td className="text-gray-600">{cName(o.company_id)}</td>
                <td className="text-gray-600 text-xs">{o.project_id ? pName(o.project_id) : "—"}</td>
                <td><span className={`chip ${STAGE_STYLE[o.stage]}`}>{o.stage}</span></td>
                <td className="font-bold text-[#004B87]">{fmtMoney(o.value_usd)}</td>
                <td className="text-gray-500">{o.probability}%</td>
                <td className="text-right">
                  <button onClick={() => generatePdf(o)} disabled={generatingPdf === o.id}
                    className="text-gray-400 hover:text-[#004B87] mr-3" title="Generate PDF quote"
                    data-testid={`quote-pdf-${o.id}`}>
                    <FileDown size={14} />
                  </button>
                  <button onClick={() => openEdit(o)} className="text-gray-400 hover:text-[#004B87] mr-3"><Pencil size={14} /></button>
                  <button onClick={() => remove(o.id)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-gray-400 py-12">No opportunities</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            data-testid="opportunity-form-modal"
            className="bg-white border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
                <div className="font-display text-xl font-bold mt-1">Opportunity</div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-black"><X size={20} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-6">
              <Field label="Title" required full>
                <input className="input-stark" required value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="opportunity-title-input" />
              </Field>
              <Field label="Company" required>
                <select className="input-stark" required value={form.company_id}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                  <option value="">—</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Project (optional)">
                <select className="input-stark" value={form.project_id || ""}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                  <option value="">—</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Stage">
                <select className="input-stark" value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}>
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Value (USD)">
                <input className="input-stark" type="number" value={form.value_usd}
                  onChange={(e) => setForm({ ...form, value_usd: e.target.value })} />
              </Field>
              <Field label="Probability (%)">
                <input className="input-stark" type="number" min={0} max={100} value={form.probability}
                  onChange={(e) => setForm({ ...form, probability: e.target.value })} />
              </Field>
              <Field label="Expected close">
                <input className="input-stark" type="date" value={form.expected_close ? form.expected_close.slice(0, 10) : ""}
                  onChange={(e) => setForm({ ...form, expected_close: e.target.value })} />
              </Field>
              <Field label="Linked products">
                <select multiple className="input-stark min-h-[80px]" value={form.product_ids}
                  onChange={(e) => setForm({
                    ...form,
                    product_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
                  })}>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Notes" full>
                <textarea className="input-stark min-h-[100px]" value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              {editing && (
                <button type="button"
                  onClick={() => { setShowModal(false); generatePdf(editing); }}
                  className="btn-ghost"
                  data-testid="quote-pdf-modal-button">
                  <FileDown size={14} /> Generate Quote PDF
                </button>
              )}
              <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-opportunity-button">
                {editing ? "Save changes" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      <QuoteBuilder
        open={!!quoteFor}
        opportunity={quoteFor}
        products={products}
        companies={companies}
        onClose={() => { setQuoteFor(null); load(); }}
      />
    </div>
  );
}
